import { Application } from '@/types/application';
import { BackendNode } from '@/types/backendNode';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError } from '@/types/validation';
import { getSubroutineByNode } from '@/util/application';
import { isArityBound } from '@/util/arity';
import { findAppStartNodes, isStartBlock } from '@/util/blocks';
import { isNumber, isString } from '@/util/typeGuards';
import { argumentsUtil } from './arguments';
import { validateConnections } from './connections';
import { enumUtil } from './enum';
import { kvValueListUtil } from './kvvaluelist';
import { kvVariablesListUtil } from './kvvariableslist';
import { multipleUtil } from './multiple';
import { promptlistUtil } from './promptlist';
import { queueUtil } from './queue';
import { queueMessageUtil } from './queuemessage';
import { returnvaluesUtil } from './returnvalues';
import { returnvariablesUtil } from './returnvariables';
import { semaphoreUtil } from './semaphore';
import { signalUtil } from './signal';
import { subroutineUtil } from './subroutines';
import { synceventUtil as syncEventUtil } from './syncevent';
import { textblockUtil } from './textblock';
import { userEventUtil } from './userevent';
import { blockTypeExists } from './validation';
import { valueUtil } from './value';
import { valueListUtil } from './valuelist';
import { variableUtil } from './variable';
import { variablesListUtil } from './variableslist';

// The engine dispatches app entry to the appstart block, so a second one is unreachable and reported.
export const validateAppStartCount = (application: Application, metadata: Metadata): ValidationError[] => {
  const appStartNodes = findAppStartNodes(application.blocks.nodes, metadata);
  if (appStartNodes.length <= 1) return [];

  return appStartNodes.map((nodeId) => {
    const node = application.blocks.nodes[nodeId];
    return {
      severity: 'error',
      message: `Block ${nodeId}: ${node.type}${node.label && ` (${node.label})`} is one of ${appStartNodes.length} app start blocks; an application may only have one`,
      subroutineId: getSubroutineByNode(application, nodeId.toString())?.id
    };
  });
};

export const validateBlocks = (
  application: Application,
  metadata: Metadata,
  blockIds: number[] | string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];
  for (const nodeId of blockIds) {
    const nodeIdNum = isString(nodeId) ? parseInt(nodeId) : nodeId;
    const node = application.blocks.nodes[nodeIdNum];
    if (node && node.type !== 'Note') {
      // Don't validate notes.
      const blockErrors = validateBlock(application, metadata, node, nodeIdNum);
      if (blockErrors.length) {
        errors.push(...blockErrors);
      }
    }
  }
  return errors;
};

export const validateDetail = (
  application: Application,
  metadata: Metadata,
  linkedSubroutineId: number,
  nodeId: number,
  blockName: string,
  configuration: Configuration,
  details: Detail
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const allArgs: [Application, Metadata, number, number, string, Configuration, Detail] = [
    application,
    metadata,
    linkedSubroutineId,
    nodeId,
    blockName,
    configuration,
    details
  ];

  switch (configuration.type) {
    case 'value': {
      const valueErrors = valueUtil().validate(...allArgs);
      if (valueErrors.length) {
        errors.push(...valueErrors);
      }
      break;
    }
    case 'textblock': {
      const textblockErrors = textblockUtil().validate(...allArgs);
      if (textblockErrors.length) {
        errors.push(...textblockErrors);
      }
      break;
    }
    case 'enum': {
      const enumErrors = enumUtil().validate(...allArgs);
      if (enumErrors.length) {
        errors.push(...enumErrors);
      }
      break;
    }
    case 'prompts': {
      const promptlistErrors = promptlistUtil().validate(...allArgs);
      if (promptlistErrors.length) {
        errors.push(...promptlistErrors);
      }
      break;
    }
    case 'multiple': {
      const multipleErrors = multipleUtil().validate(...allArgs);
      if (multipleErrors.length) {
        errors.push(...multipleErrors);
      }
      break;
    }
    case 'queue': {
      const queueErrors = queueUtil().validate(...allArgs);
      if (queueErrors.length) {
        errors.push(...queueErrors);
      }
      break;
    }
    case 'queuemessage': {
      const queueMessageErrors = queueMessageUtil().validate(...allArgs);
      if (queueMessageErrors.length) {
        errors.push(...queueMessageErrors);
      }
      break;
    }
    case 'variable': {
      const variableErrors = variableUtil().validate(...allArgs);
      if (variableErrors.length) {
        errors.push(...variableErrors);
      }
      break;
    }
    case 'variableslist': {
      const variableslistErrors = variablesListUtil().validate(...allArgs);
      if (variableslistErrors.length) {
        errors.push(...variableslistErrors);
      }
      break;
    }
    case 'kvvariableslist': {
      const kvVariableslistErrors = kvVariablesListUtil().validate(...allArgs);
      if (kvVariableslistErrors.length) {
        errors.push(...kvVariableslistErrors);
      }
      break;
    }
    case 'valuelist': {
      const valuelistErrors = valueListUtil().validate(...allArgs);
      if (valuelistErrors.length) {
        errors.push(...valuelistErrors);
      }
      break;
    }
    case 'kvvaluelist': {
      const kvValuelistErrors = kvValueListUtil().validate(...allArgs);
      if (kvValuelistErrors.length) {
        errors.push(...kvValuelistErrors);
      }
      break;
    }
    case 'subroutine':
    case 'eventhandler': {
      const subEventErrors = subroutineUtil().validate(...allArgs);
      if (subEventErrors.length) {
        errors.push(...subEventErrors);
      }
      break;
    }
    case 'arguments': {
      const argumentsErrors = argumentsUtil().validate(...allArgs);
      if (argumentsErrors.length) {
        errors.push(...argumentsErrors);
      }
      break;
    }
    case 'returnvariables': {
      const returnVarsErrors = returnvariablesUtil().validate(...allArgs);
      if (returnVarsErrors.length) {
        errors.push(...returnVarsErrors);
      }
      break;
    }
    case 'returnvalues':
    case 'appreturnvalues': {
      const returnValuesErrors = returnvaluesUtil().validate(...allArgs);
      if (returnValuesErrors.length) {
        errors.push(...returnValuesErrors);
      }
      break;
    }
    case 'signal': {
      const signalErrors = signalUtil().validate(...allArgs);
      if (signalErrors.length) {
        errors.push(...signalErrors);
      }
      break;
    }
    case 'semaphore': {
      const semaphoreErrors = semaphoreUtil().validate(...allArgs);
      if (semaphoreErrors.length) {
        errors.push(...semaphoreErrors);
      }
      break;
    }
    case 'syncevent': {
      const syncEventErrors = syncEventUtil().validate(...allArgs);
      if (syncEventErrors.length) {
        errors.push(...syncEventErrors);
      }
      break;
    }
    case 'userevent': {
      const userEventErrors = userEventUtil().validate(...allArgs);
      if (userEventErrors.length) {
        errors.push(...userEventErrors);
      }
      break;
    }
    case 'passthrough':
    default:
      break;
  }
  return errors;
};
export const validateBlock = (
  application: Application,
  metadata: Metadata,
  node: BackendNode,
  nodeId: number
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const blockName = `${nodeId}: ${node.type}${node.label && ` (${node.label})`}`;
  if (!blockTypeExists(metadata, node.type)) {
    errors.push({
      severity: 'error',
      message: `Block ${blockName} refers to a type that doesn't exist in metadata`,
      id: nodeId
    });
  }

  const linkedSubroutine = application.subroutines.find((subroutine) =>
    subroutine.nodes.some((nodeId_) => nodeId_ === nodeId)
  );
  const metaBlock = metadata.blocks.find((block) => block.type === node.type);

  if (metaBlock?.obsolete) {
    errors.push({
      severity: 'warning',
      message: `Block ${blockName} is obsolete: ${metaBlock?.obsolete}`,
      id: nodeId
    });
  }

  metaBlock?.event_handlers?.forEach((eventHandler) => {
    const appEventHandler = application.blocks.event_handlers?.[nodeId]?.[eventHandler.name];

    eventHandler.args
      ?.filter((arg) => arg.required && !isArityBound(arg))
      .forEach((requiredArg) => {
        if (appEventHandler?.[requiredArg.value]) return;

        errors.push({
          severity: 'error',
          message: `Block ${blockName} is missing required event handler: ${eventHandler.name}`,
          id: nodeId
        });
      });

    if (!appEventHandler || !linkedSubroutine) return;
    eventHandler.args
      ?.filter(isArityBound)
      .forEach((arg) =>
        errors.push(
          ...validateDetail(
            application,
            metadata,
            linkedSubroutine.id,
            nodeId,
            blockName,
            { ...arg, event_name: eventHandler.name } as Configuration,
            appEventHandler as unknown as Detail
          )
        )
      );
  });

  const connectionErrors = validateConnections(application.blocks.connections[nodeId] ?? [], nodeId, blockName);
  if (connectionErrors.length) {
    errors.push(...connectionErrors);
  }

  const metadataPorts = metaBlock?.output_ports;
  const portoptionIndex = application.blocks.portoptions[nodeId]?.index;
  if (metadataPorts?.length && metadataPorts.length >= 2 && isNumber(portoptionIndex)) {
    const outputPort = metadataPorts[portoptionIndex];

    if (outputPort.type === 'nothing') {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is set to a nonexistant port option.`,
        id: nodeId
      });
    }
  }

  if (!linkedSubroutine?.id) {
    errors.push({
      severity: 'error',
      message: `Block ${blockName} has no connected subroutine.`,
      id: nodeId
    });
  } else {
    isStartBlock(node) &&
      [...linkedSubroutine.arg_bindings, ...(linkedSubroutine.event_arg_bindings ?? [])].forEach((binding) => {
        if (
          binding &&
          !application.variables.local[linkedSubroutine.id]?.find((localVariable) => localVariable.name === binding)
        ) {
          errors.push({
            severity: 'error',
            message: `Block ${blockName} is referring to a variable that doesn't exist`,
            id: nodeId
          });
        }
      });

    const details = application.blocks.details[nodeId];
    if (details && (!metaBlock || !blockTypeExists(metadata, node.type))) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} refers to a type that doesn't exist in metadata`,
        id: nodeId
      });
    } else if (metaBlock) {
      const configurations = details
        ? metaBlock.configuration
        : (metaBlock.configuration as Configuration[]).filter(isArityBound);
      const validatedDetails = configurations
        .map((configuration) =>
          validateDetail(application, metadata, linkedSubroutine.id, nodeId, blockName, configuration, details ?? {})
        )
        .flat();
      if (validatedDetails.length) {
        errors.push(...validatedDetails);
      }
    }
  }
  return errors;
};
